import '../types';
import { callOrReturn, getExtensionField, mergeAttributes, Node, } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { addColumnAfter, addColumnBefore, addRowAfter, addRowBefore, CellSelection, columnResizing, deleteColumn, deleteRow, deleteTable, fixTables, goToNextCell, mergeCells, setCellAttr, splitCell, TableMap, tableEditing, toggleHeader, toggleHeaderCell, } from '@tiptap/pm/tables';
import { parseStyleString, responsiveVarName, stringifyStyleObject } from '@/plugins/tiptap-v3/functionality/responsive/responsive';
import { TableView } from './TableView';
import { createColGroup } from './utilities/createColGroup';
import { createTable } from './utilities/createTable';
import { deleteTableWhenAllCellsSelected } from './utilities/deleteTableWhenAllCellsSelected';
import renderTableToMarkdown from './utilities/markdown';
import { getResponsiveColWidthValue, setResponsiveColWidthValue, unsetResponsiveColWidthValue, } from './utilities/responsiveColWidth';
function getBreakpoint(editor) {
    var _a, _b;
    return ((_b = (_a = editor === null || editor === void 0 ? void 0 : editor.storage) === null || _a === void 0 ? void 0 : _a.responsiveEngine) === null || _b === void 0 ? void 0 : _b.breakpoint) || 'lg';
}
function getResponsiveConfig(options) {
    if (!options.responsive || !options.responsive.enabled)
        return null;
    return {
        prefix: options.responsive.prefix || 'vp',
        cssPropWidth: options.responsive.cssPropWidth || 'table-width',
    };
}
function findTablePos(editor, tableTypeName) {
    var _a, _b, _c, _d;
    const sel = (_a = editor === null || editor === void 0 ? void 0 : editor.state) === null || _a === void 0 ? void 0 : _a.selection;
    if (!sel)
        return null;
    if (((_c = (_b = sel.node) === null || _b === void 0 ? void 0 : _b.type) === null || _c === void 0 ? void 0 : _c.name) === tableTypeName) {
        return typeof sel.from === 'number' ? sel.from : null;
    }
    const $from = sel.$from;
    if (!$from)
        return null;
    for (let depth = $from.depth; depth > 0; depth -= 1) {
        const node = $from.node(depth);
        if (((_d = node.type) === null || _d === void 0 ? void 0 : _d.name) === tableTypeName) {
            return $from.before(depth);
        }
    }
    return null;
}
/**
 * This extension allows you to create tables.
 * @see https://www.tiptap.dev/api/nodes/table
 */
export const Table = Node.create({
    name: 'table',
    // @ts-ignore
    addOptions() {
        return {
            HTMLAttributes: {},
            resizable: false,
            renderWrapper: false,
            handleWidth: 5,
            cellMinWidth: 25,
            // TODO: fix
            View: TableView,
            lastColumnResizable: true,
            allowTableNodeSelection: false,
            responsive: {
                enabled: true,
                prefix: 'vp',
                cssPropWidth: 'table-width',
            },
        };
    },
    content: 'tableRow+',
    tableRole: 'table',
    isolating: true,
    group: 'block',
    addAttributes() {
        return {
            style: {
                default: null,
                parseHTML: (element) => element.getAttribute('style'),
                renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
            },
        };
    },
    parseHTML() {
        return [{ tag: 'table' }];
    },
    renderHTML({ node, HTMLAttributes }) {
        const { colgroup, tableWidth, tableMinWidth } = createColGroup(node, this.options.cellMinWidth, {
            breakpoint: getBreakpoint(this.editor),
        });
        const userStyles = HTMLAttributes.style;
        function getTableStyle() {
            if (!userStyles) {
                return tableWidth ? `width: ${tableWidth}` : `min-width: ${tableMinWidth}`;
            }
            const styleObj = parseStyleString(userStyles);
            const hasUserWidth = typeof styleObj.width === 'string' && styleObj.width.trim().length > 0;
            const hasUserMinWidth = typeof styleObj['min-width'] === 'string' && styleObj['min-width'].trim().length > 0;
            if (!hasUserWidth && tableWidth) {
                styleObj.width = tableWidth;
            }
            if (!hasUserMinWidth && tableMinWidth) {
                styleObj['min-width'] = tableMinWidth;
            }
            return stringifyStyleObject(styleObj);
        }
        const table = [
            'table',
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
                style: getTableStyle(),
            }),
            colgroup,
            ['tbody', 0],
        ];
        return this.options.renderWrapper ? ['div', { class: 'tableWrapper' }, table] : table;
    },
    parseMarkdown: (token, h) => {
        const rows = [];
        if (token.header) {
            const headerCells = [];
            token.header.forEach(cell => {
                headerCells.push(h.createNode('tableHeader', {}, [{ type: 'paragraph', content: h.parseInline(cell.tokens) }]));
            });
            rows.push(h.createNode('tableRow', {}, headerCells));
        }
        if (token.rows) {
            token.rows.forEach(row => {
                const bodyCells = [];
                row.forEach(cell => {
                    bodyCells.push(h.createNode('tableCell', {}, [{ type: 'paragraph', content: h.parseInline(cell.tokens) }]));
                });
                rows.push(h.createNode('tableRow', {}, bodyCells));
            });
        }
        return h.createNode('table', undefined, rows);
    },
    renderMarkdown: (node, h) => {
        return renderTableToMarkdown(node, h);
    },
    addCommands() {
        return {
            insertTable: ({ rows = 3, cols = 3, withHeaderRow = true } = {}) => ({ tr, dispatch, editor }) => {
                const node = createTable(editor.schema, rows, cols, withHeaderRow);
                if (dispatch) {
                    const offset = tr.selection.from + 1;
                    tr.replaceSelectionWith(node)
                        .scrollIntoView()
                        .setSelection(TextSelection.near(tr.doc.resolve(offset)));
                }
                return true;
            },
            setTableWidth: (width, opts) => ({ editor, tr, dispatch }) => {
                var _a, _b;
                const tablePos = findTablePos(editor, this.name);
                if (tablePos == null)
                    return false;
                const tableNode = tr.doc.nodeAt(tablePos);
                if (!tableNode || ((_a = tableNode.type) === null || _a === void 0 ? void 0 : _a.name) !== this.name)
                    return false;
                const styleObj = parseStyleString(((_b = tableNode.attrs) === null || _b === void 0 ? void 0 : _b.style) || '');
                const cfg = getResponsiveConfig(this.options);
                if (!cfg) {
                    styleObj.width = width;
                }
                else {
                    const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                    const widthVar = responsiveVarName(cfg.cssPropWidth, bp, cfg.prefix);
                    // Legacy compatibility: remove fixed width so responsive vars are authoritative.
                    delete styleObj.width;
                    styleObj[widthVar] = width;
                }
                if (dispatch) {
                    tr.setNodeMarkup(tablePos, undefined, {
                        ...tableNode.attrs,
                        style: stringifyStyleObject(styleObj) || null,
                    });
                }
                return true;
            },
            unsetTableWidth: (opts) => ({ editor, tr, dispatch }) => {
                var _a, _b;
                const tablePos = findTablePos(editor, this.name);
                if (tablePos == null)
                    return false;
                const tableNode = tr.doc.nodeAt(tablePos);
                if (!tableNode || ((_a = tableNode.type) === null || _a === void 0 ? void 0 : _a.name) !== this.name)
                    return false;
                const styleObj = parseStyleString(((_b = tableNode.attrs) === null || _b === void 0 ? void 0 : _b.style) || '');
                const cfg = getResponsiveConfig(this.options);
                // Always clear legacy fixed width.
                delete styleObj.width;
                if (cfg) {
                    const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                    const widthVar = responsiveVarName(cfg.cssPropWidth, bp, cfg.prefix);
                    delete styleObj[widthVar];
                }
                if (dispatch) {
                    tr.setNodeMarkup(tablePos, undefined, {
                        ...tableNode.attrs,
                        style: stringifyStyleObject(styleObj) || null,
                    });
                }
                return true;
            },
            getTableWidth: (opts) => ({ editor }) => {
                var _a, _b;
                const tablePos = findTablePos(editor, this.name);
                if (tablePos == null)
                    return null;
                const tableNode = editor.state.doc.nodeAt(tablePos);
                if (!tableNode || ((_a = tableNode.type) === null || _a === void 0 ? void 0 : _a.name) !== this.name)
                    return null;
                const styleObj = parseStyleString(((_b = tableNode.attrs) === null || _b === void 0 ? void 0 : _b.style) || '');
                const cfg = getResponsiveConfig(this.options);
                if (!cfg) {
                    return (styleObj.width || null);
                }
                const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                const widthVar = responsiveVarName(cfg.cssPropWidth, bp, cfg.prefix);
                return (styleObj[widthVar] || null);
            },
            setTableColumnWidth: (column, width, opts) => ({ editor, tr, dispatch }) => {
                var _a, _b, _c;
                const tablePos = findTablePos(editor, this.name);
                if (tablePos == null)
                    return false;
                const tableNode = tr.doc.nodeAt(tablePos);
                if (!tableNode || ((_a = tableNode.type) === null || _a === void 0 ? void 0 : _a.name) !== this.name)
                    return false;
                const map = TableMap.get(tableNode);
                if (!Number.isInteger(column) || column < 0 || column >= map.width)
                    return false;
                const cfg = getResponsiveConfig(this.options);
                if (!cfg)
                    return false;
                const nextStyle = setResponsiveColWidthValue(((_b = tableNode.attrs) === null || _b === void 0 ? void 0 : _b.style) || '', column, width, {
                    editor,
                    domElement: (_c = editor === null || editor === void 0 ? void 0 : editor.view) === null || _c === void 0 ? void 0 : _c.dom,
                    breakpoint: opts === null || opts === void 0 ? void 0 : opts.breakpoint,
                    prefix: cfg.prefix,
                });
                if (dispatch) {
                    tr.setNodeMarkup(tablePos, undefined, {
                        ...tableNode.attrs,
                        style: nextStyle || null,
                    });
                }
                return true;
            },
            unsetTableColumnWidth: (column, opts) => ({ editor, tr, dispatch }) => {
                var _a, _b, _c;
                const tablePos = findTablePos(editor, this.name);
                if (tablePos == null)
                    return false;
                const tableNode = tr.doc.nodeAt(tablePos);
                if (!tableNode || ((_a = tableNode.type) === null || _a === void 0 ? void 0 : _a.name) !== this.name)
                    return false;
                const map = TableMap.get(tableNode);
                if (!Number.isInteger(column) || column < 0 || column >= map.width)
                    return false;
                const cfg = getResponsiveConfig(this.options);
                if (!cfg)
                    return false;
                const nextStyle = unsetResponsiveColWidthValue(((_b = tableNode.attrs) === null || _b === void 0 ? void 0 : _b.style) || '', column, {
                    editor,
                    domElement: (_c = editor === null || editor === void 0 ? void 0 : editor.view) === null || _c === void 0 ? void 0 : _c.dom,
                    breakpoint: opts === null || opts === void 0 ? void 0 : opts.breakpoint,
                    prefix: cfg.prefix,
                });
                if (dispatch) {
                    tr.setNodeMarkup(tablePos, undefined, {
                        ...tableNode.attrs,
                        style: nextStyle || null,
                    });
                }
                return true;
            },
            getTableColumnWidth: (column, opts) => ({ editor }) => {
                var _a, _b, _c;
                const tablePos = findTablePos(editor, this.name);
                if (tablePos == null)
                    return null;
                const tableNode = editor.state.doc.nodeAt(tablePos);
                if (!tableNode || ((_a = tableNode.type) === null || _a === void 0 ? void 0 : _a.name) !== this.name)
                    return null;
                const map = TableMap.get(tableNode);
                if (!Number.isInteger(column) || column < 0 || column >= map.width)
                    return null;
                const cfg = getResponsiveConfig(this.options);
                if (!cfg)
                    return null;
                return getResponsiveColWidthValue(((_b = tableNode.attrs) === null || _b === void 0 ? void 0 : _b.style) || '', column, {
                    editor,
                    domElement: (_c = editor === null || editor === void 0 ? void 0 : editor.view) === null || _c === void 0 ? void 0 : _c.dom,
                    breakpoint: opts === null || opts === void 0 ? void 0 : opts.breakpoint,
                    prefix: cfg.prefix,
                });
            },
            addColumnBefore: () => ({ state, dispatch }) => {
                return addColumnBefore(state, dispatch);
            },
            addColumnAfter: () => ({ state, dispatch }) => {
                return addColumnAfter(state, dispatch);
            },
            deleteColumn: () => ({ state, dispatch }) => {
                return deleteColumn(state, dispatch);
            },
            addRowBefore: () => ({ state, dispatch }) => {
                return addRowBefore(state, dispatch);
            },
            addRowAfter: () => ({ state, dispatch }) => {
                return addRowAfter(state, dispatch);
            },
            deleteRow: () => ({ state, dispatch }) => {
                return deleteRow(state, dispatch);
            },
            deleteTable: () => ({ state, dispatch }) => {
                return deleteTable(state, dispatch);
            },
            mergeCells: () => ({ state, dispatch }) => {
                return mergeCells(state, dispatch);
            },
            splitCell: () => ({ state, dispatch }) => {
                return splitCell(state, dispatch);
            },
            toggleHeaderColumn: () => ({ state, dispatch }) => {
                return toggleHeader('column')(state, dispatch);
            },
            toggleHeaderRow: () => ({ state, dispatch }) => {
                return toggleHeader('row')(state, dispatch);
            },
            toggleHeaderCell: () => ({ state, dispatch }) => {
                return toggleHeaderCell(state, dispatch);
            },
            mergeOrSplit: () => ({ state, dispatch }) => {
                if (mergeCells(state, dispatch)) {
                    return true;
                }
                return splitCell(state, dispatch);
            },
            setCellAttribute: (name, value) => ({ state, dispatch }) => {
                return setCellAttr(name, value)(state, dispatch);
            },
            goToNextCell: () => ({ state, dispatch }) => {
                return goToNextCell(1)(state, dispatch);
            },
            goToPreviousCell: () => ({ state, dispatch }) => {
                return goToNextCell(-1)(state, dispatch);
            },
            fixTables: () => ({ state, dispatch }) => {
                if (dispatch) {
                    fixTables(state);
                }
                return true;
            },
            setCellSelection: position => ({ tr, dispatch }) => {
                if (dispatch) {
                    const selection = CellSelection.create(tr.doc, position.anchorCell, position.headCell);
                    // @ts-ignore
                    tr.setSelection(selection);
                }
                return true;
            },
        };
    },
    addKeyboardShortcuts() {
        return {
            Tab: () => {
                if (this.editor.commands.goToNextCell()) {
                    return true;
                }
                if (!this.editor.can().addRowAfter()) {
                    return false;
                }
                return this.editor.chain().addRowAfter().goToNextCell().run();
            },
            'Shift-Tab': () => this.editor.commands.goToPreviousCell(),
            Backspace: deleteTableWhenAllCellsSelected,
            'Mod-Backspace': deleteTableWhenAllCellsSelected,
            Delete: deleteTableWhenAllCellsSelected,
            'Mod-Delete': deleteTableWhenAllCellsSelected,
        };
    },
    addProseMirrorPlugins() {
        const isResizable = this.options.resizable && this.editor.isEditable;
        return [
            ...(isResizable
                ? [
                    columnResizing({
                        handleWidth: this.options.handleWidth,
                        cellMinWidth: this.options.cellMinWidth,
                        defaultCellMinWidth: this.options.cellMinWidth,
                        // @ts-ignore
                        View: this.options.View,
                        lastColumnResizable: this.options.lastColumnResizable,
                    }),
                ]
                : []),
            tableEditing({
                allowTableNodeSelection: this.options.allowTableNodeSelection,
            }),
        ];
    },
    extendNodeSchema(extension) {
        const context = {
            name: extension.name,
            options: extension.options,
            storage: extension.storage,
        };
        return {
            tableRole: callOrReturn(getExtensionField(extension, 'tableRole', context)),
        };
    },
});
